import type { CSSProperties } from "react";

/**
 * Der App-Name legt sich beim Oeffnen Zeichen fuer Zeichen frei.
 *
 * ── WARUM EIN DECKEL UND KEIN `clip-path` ───────────────────────────────────
 *
 * Die Freilegung lief bis 0.288.0 als `clip-path: inset(0 100% 0 0) → inset(0)`
 * auf dem Schriftzug selbst. Sie war korrekt und trotzdem unsichtbar: `clip-path`
 * ist in Chromium keine Eigenschaft, die der Compositor selbst fortschreibt, sie
 * haengt am Hauptthread — und der ist beim Oeffnen einer App genau dann dicht,
 * wenn die Animation laufen soll.
 *
 * Gemessen am 2026-08-22 aus einem Screencast von chat.janewaylabs.com, Bild fuer
 * Bild ausgewertet („AI Chat", 104 px breit, `steps(7)` → Stufen bei 15, 30, 45,
 * 59, 74, 89, 104 px):
 *
 *   633 ms   nichts
 *   649 ms   Stufe 4 (60 px) — die Stufen 1-3 wurden nie gezeichnet
 *   …        Stufe 4 steht 102 ms lang still, ueber sieben gezeichnete Bilder
 *   768 ms   Stufe 7 (104 px) — die Stufen 5 und 6 wurden nie gezeichnet
 *
 * Fuenf von sieben Stufen kamen also nie ins Bild, und die Bilder wurden in der
 * Zeit sehr wohl gezeichnet (alle 16 ms eines). Gegenprobe auf einer leeren
 * Seite, Hauptthread absichtlich 900 ms blockiert, beide Mechaniken nebeneinander:
 *
 *   | t       | `clip-path` | Deckel mit `transform` |
 *   |---------|-------------|------------------------|
 *   | 444 ms  |   0 px      |    44 px               |
 *   | 744 ms  |   0 px      |   173 px               |
 *   | 1244 ms |   0 px      |   390 px               |
 *   | 1316 ms | 386 px      |   390 px               |
 *
 * `clip-path` steht die ganze Blockade still und springt danach ans Ende; der
 * Deckel laeuft durch. Deshalb liegt die Animation heute auf `transform` — der
 * Compositor schreibt sie fort, ohne den Hauptthread zu fragen.
 *
 * ── WARUM DER DECKEL IM RUHEZUSTAND WEG IST ─────────────────────────────────
 *
 * Der Ruhezustand ist `translateX(101%)`, also freigelegt, und die Keyframes
 * laufen VON verdeckt DORTHIN (`animation-fill-mode: backwards` haelt waehrend
 * der Verzoegerung den Anfang). Andersherum waere naheliegender, hat aber einen
 * Totalausfall als Fehlerbild: laeuft die Animation aus irgendeinem Grund nicht,
 * bliebe der Deckel liegen und der App-Name waere dauerhaft unsichtbar. So
 * herum ist der Ausfall, dass es nicht animiert — und der Name steht da.
 *
 * ── WARUM NICHT AUF DEM SCHRIFTZUG SELBST ───────────────────────────────────
 *
 * Ein frueherer Anlauf setzte die Freilegung auf ein KIND des Sheen-Elements.
 * Der Sheen setzt `color: transparent` und malt den Namen ueber
 * `background-clip: text` — sichtbar ist der HINTERGRUND des Elements, nicht
 * sein Text; beschnitten wurde also nur transparenter Text. Web Animations API
 * und DOM meldeten dabei einwandfreie Werte (70 → 385 ms, 29 % / 71 % frei), im
 * Bild stand bis 355 ms nichts und bei 389 ms der volle Name.
 *
 * Der Deckel loest das nebenbei mit: er verdeckt, was gemalt wird, egal WIE es
 * gemalt wird. Und er nimmt dem Schriftzug die Inline-`animation-*` wieder ab —
 * die schlug jede Regel aus dem Stylesheet und hatte damit `.wordmark-sheen:hover`
 * stillgelegt, also den Glint beim Ueberfahren, in jeder App mit Auftritt.
 *
 * ── WARUM CSS UND NICHT EIN ZUSTAND IN REACT ────────────────────────────────
 *
 * Ein Zaehler in `useState`, gestartet aus einem Effekt, begann gemessen erst
 * bei 536 ms: vor der Hydration laeuft kein Effekt, der Name steht zu dem
 * Zeitpunkt aber schon eine halbe Sekunde aus dem Server-Markup im Bild. Er
 * verschwand also und baute sich erneut auf — ein Rueckschritt, kein Auftritt.
 * CSS laeuft, sobald der Browser zeichnet.
 *
 * Die Keyframes liefert `TIPP_KEYFRAMES` mit — dasselbe Muster wie
 * `BarSpinner`. Der Baustein bringt mit, was er braucht, statt eine Zeile in
 * einer Datei zu belegen, an der in diesem Baum staendig jemand schreibt.
 */

/**
 * Zeit je Zeichen.
 *
 * 21 ms waren zu schnell: „AI Chat" war nach 147 ms durch, und das liest sich
 * als Wisch, nicht als Schreiben. Unter ~30 ms sieht man die Stufen nicht mehr
 * einzeln. Nach oben begrenzt es der erste Paint (368-1380 ms auf Labs), auf
 * den sich alles hier addiert: 38 ms ergeben fuer „AI Chat" 266 ms, und das
 * ist der laengste Einzelposten der ganzen Kopfzeile.
 */
export const TIPP_JE_ZEICHEN_MS = 38;

/**
 * Obergrenze der Gesamtdauer. Ohne sie daempft ein langer App-Name die ganze
 * Kopfzeile aus — „Janeway Automations" waere bei 855 ms. Die Stufenzahl bleibt
 * die Zeichenzahl; nur die Zeit je Stufe wird enger.
 */
export const TIPP_HOECHSTDAUER_MS = 300;

/**
 * Die Keyframes UND die Abbestellung. Beides gehoert EINMAL in den Baum, in dem
 * `tippStil` benutzt wird — ein zweites `<style>` desselben Inhalts ist harmlos,
 * aber ueberfluessig.
 *
 * 101 % statt 100 %: bei gebrochenen Elementbreiten bliebe sonst eine
 * Subpixel-Spalte des Deckels ueber dem letzten Zeichen stehen.
 *
 * WARUM DIE ABBESTELLUNG HIER STEHT UND NICHT IN `globals.css`: `tippStil` ist
 * ein Inline-Stil, und ein Inline-Stil schlaegt jede normale Regel — die
 * Bibliothek haelt ihre Auftritte sonst in `@media (prefers-reduced-motion:
 * no-preference)`, und genau daran lief diese eine Animation vorbei. Nur
 * `!important` aus einem Stylesheet kommt an einen Inline-Stil heran. Der
 * Ruhezustand des Deckels ist „weg", `animation: none` legt den Namen also
 * sofort frei, statt ihn zu verdecken.
 */
export const TIPP_KEYFRAMES =
  "@keyframes jwTippen{from{transform:translateX(0)}to{transform:translateX(101%)}}" +
  "@media (prefers-reduced-motion:reduce){[data-jw-tippdeckel]{animation:none!important}}";

/**
 * Der Stil fuer den Deckel. Gehoert auf ein leeres, `aria-hidden`-Element
 * INNERHALB des Elements, das den Namen zeigt — das muss `relative` und
 * `overflow: hidden` sein, sonst schiebt der Deckel sich ueber den Nachbarn.
 *
 * @example
 *   <span className="wordmark-sheen relative truncate">
 *     {name}
 *     <span aria-hidden data-jw-tippdeckel="" style={tippStil(name, 60)} />
 *   </span>
 */
export function tippStil(text: string, abMs = 0): CSSProperties {
  const zeichen = Math.max(text.length, 1);
  return {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    // DIE FLAECHE DES BEREICHS, NICHT DIE DES THEMES. Bis 0.467.0 stand hier
    // `var(--background)` mit der Begruendung „dieselbe Variable, aus der die
    // Kopfzeile ihr `bg-background` nimmt". Das stimmte fuer Shell 1.0 und ist
    // unter „umklammert" falsch: dort malt der Rahmen `--jw-grund`, waehrend
    // `--background` im Rahmen unveraendert der Theme-Ton bleibt. Am
    // 2026-09-20 an gui.janewaylabs.com/shell gemessen, janeway hell:
    // Rahmen rgb(0,0,0), Deckel rgb(250,250,250) — 21 : 1, 114 ms lang, bei
    // JEDEM Oeffnen, in allen sechs geprueften Themes. Der Betreiber hat es
    // als „Flackerkasten" gemeldet.
    //
    // Ein Inline-Stil ist fuer keine CSS-Regel erreichbar; die Korrektur kann
    // nur hier stehen (s. der Absatz zur Abbestellung weiter oben).
    background: "var(--jw-flaeche-hier, var(--background))",
    // Ruhezustand: weg. Das Fehlerbild einer nicht laufenden Animation ist
    // damit „kein Auftritt", nicht „kein App-Name".
    transform: "translateX(101%)",
    animationName: "jwTippen",
    animationDuration: `${Math.min(zeichen * TIPP_JE_ZEICHEN_MS, TIPP_HOECHSTDAUER_MS)}ms`,
    animationTimingFunction: `steps(${zeichen}, end)`,
    animationDelay: `${abMs}ms`,
    // Haelt den Anfangszustand waehrend der Verzoegerung. Ohne das stuende der
    // volle Name da und wuerde dann weggenommen.
    animationFillMode: "backwards",
  };
}
